import { gql } from '@apollo/client';
import { getAppliedMonetaryBonusLabels, getPrimaryQuantityBonusLabel } from 'helpers/bonusHelper';
import { useEffect, useMemo, useQuery } from 'libs/react';
/**
 * Number of vouchers of each line (quantity plus bonus units, same rule as
 * the vouchers created after payment), the value of each voucher (AMOUNT and
 * PERCENTAGE bonuses included) and the product's bonuses for the labels
 * @const StoreOfferedUnitsQuery
 * @since 1.0.0
 */
const StoreOfferedUnitsQuery = gql `
  query StoreOfferedUnits($input: DealInput!) {
    deal(input: $input) {
      id
      items {
        id
        quantity
        proposed
        voucherCount
        voucherAmount
        bonusVoucherAmount
        item {
          id
          bonuses {
            type
            value
            separateVoucher
            order
            active
            conditions
          }
        }
      }
    }
  }
`;
/**
 * Units a quantity bonus adds to each cart line ("buy 2, get the 3rd") and
 * extra value a monetary bonus gives each of its vouchers, by deal item id
 * @function useStoreOfferedUnits
 * @since 1.0.0
 */
export function useStoreOfferedUnits(deal) {
    var _a, _b;
    const [data, , , , refetch] = useQuery(StoreOfferedUnitsQuery, {
        variables: { input: { id: (_a = deal === null || deal === void 0 ? void 0 : deal.id) !== null && _a !== void 0 ? _a : '' } },
        skip: !(deal === null || deal === void 0 ? void 0 : deal.id),
        fetchPolicy: 'cache-and-network'
    });
    // The cart changed (quantity, line added or removed): count again
    const linesKey = ((_b = deal === null || deal === void 0 ? void 0 : deal.items) !== null && _b !== void 0 ? _b : [])
        .map((item) => `${item.id}:${item.quantity}`)
        .join(',');
    useEffect(() => {
        if ((deal === null || deal === void 0 ? void 0 : deal.id) && refetch) {
            void refetch();
        }
    }, [linesKey]);
    return useMemo(() => {
        var _a, _b, _c, _d;
        const offered = new Map();
        const monetary = new Map();
        for (const line of (_b = (_a = data === null || data === void 0 ? void 0 : data.deal) === null || _a === void 0 ? void 0 : _a.items) !== null && _b !== void 0 ? _b : []) {
            if (line.proposed) {
                continue;
            }
            const quantity = Number(line.voucherCount) - Number(line.quantity);
            if (quantity > 0) {
                offered.set(line.id, {
                    quantity,
                    label: getPrimaryQuantityBonusLabel((_c = line.item) === null || _c === void 0 ? void 0 : _c.bonuses)
                });
            }
            const labels = getAppliedMonetaryBonusLabels((_d = line.item) === null || _d === void 0 ? void 0 : _d.bonuses, Number(line.quantity));
            if (line.voucherAmount != null && labels.length > 0) {
                monetary.set(line.id, {
                    labels,
                    unitAmount: Number(line.voucherAmount),
                    bonusVoucherAmount: line.bonusVoucherAmount != null
                        ? Number(line.bonusVoucherAmount)
                        : null
                });
            }
        }
        return { offered, monetary };
    }, [data]);
}
